"use client";

import { useState } from "react";
import { X, CheckCircle2, Sparkles, PhoneCall } from "lucide-react";

interface DemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DemoModal({ isOpen, onClose }: DemoModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    leadVolume: "1,000 - 5,000 leads/mo",
  });

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    
    // Track lead creation with OpenAI
    const leadId = Date.now().toString(); // Generate a simple unique ID for testing
    if (typeof window !== "undefined" && (window as any).oaiq) {
      (window as any).oaiq("measure", "lead_created", { type: "customer_action" }, { event_id: "lead_" + leadId });
      console.log("Lead tracked with oaiq", leadId);
    }
  };

  const handleResetAndClose = () => {
    setSubmitted(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div
        className="relative w-full max-w-lg bg-[#0D172E] border border-white/15 rounded-2xl p-6 sm:p-8 shadow-2xl text-white overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Subtle decorative glow */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#F2A339]/20 rounded-full blur-3xl pointer-events-none" />

        {/* Close button */}
        <button
          onClick={handleResetAndClose}
          className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
          title="Close dialog"
        >
          <X className="w-4 h-4" />
        </button>

        {submitted ? (
          <div className="py-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto text-emerald-400">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold">Demo Request Confirmed!</h3>
            <p className="text-sm text-slate-300 max-w-md mx-auto leading-relaxed">
              Thank you, <span className="font-semibold text-white">{form.name}</span>. Our AI sales agent has queued your number{" "}
              <span className="font-mono text-[#F2A339]">{form.phone || "+1 (555) 019-2834"}</span>. You will receive a live qualification test call shortly.
            </p>
            <div className="pt-4">
              <button
                onClick={handleResetAndClose}
                className="btn-primary text-sm py-2.5 px-6 font-semibold"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-6 space-y-1">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#F2A339]">
                <Sparkles className="w-3.5 h-3.5" /> 14-Day Free Pilot
              </div>
              <h3 className="text-2xl font-bold tracking-tight">
                Book an EchoLeads Live Demo
              </h3>
              <p className="text-xs sm:text-sm text-slate-400">
                Experience the autonomous AI sales agent calling and qualifying live in under 60 seconds.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Alex Morgan"
                  className="w-full bg-[#101C38] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#F2A339] transition-colors"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Work Email
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="alex@company.com"
                    className="w-full bg-[#101C38] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#F2A339] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Phone (for AI test call)
                  </label>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    className="w-full bg-[#101C38] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#F2A339] transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    required
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Acme Real Estate"
                    className="w-full bg-[#101C38] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#F2A339] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Monthly Lead Volume
                  </label>
                  <select
                    value={form.leadVolume}
                    onChange={(e) => setForm({ ...form, leadVolume: e.target.value })}
                    className="w-full bg-[#101C38] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#F2A339] transition-colors"
                  >
                    <option value="Under 500 leads/mo">Under 500 leads/mo</option>
                    <option value="500 - 1,000 leads/mo">500 - 1,000 leads/mo</option>
                    <option value="1,000 - 5,000 leads/mo">1,000 - 5,000 leads/mo</option>
                    <option value="5,000+ leads/mo">5,000+ leads/mo</option>
                  </select>
                </div>
              </div>

              <div className="pt-3">
                <button
                  type="submit"
                  className="btn-primary w-full py-3 text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <PhoneCall className="w-4 h-4" />
                  <span>Schedule Demo &amp; Receive Test Call</span>
                </button>
              </div>

              <p className="text-[11px] text-center text-slate-400 pt-1">
                No credit card required · Instant setup in under 30 minutes
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
